/**
 * Derives the lifecycle status of a financial record from its payment state and due date.
 * 
 * Status is computed on every render from stored fields (not a static badge).
 * Overdue takes precedence over pending and partial.
 * 
 * Validates: Requirements 9.1, 9.2, 9.3, 9.4, 9.5, 9.6
 */

export type FinancialRecordStatusType = 'pending' | 'partial' | 'paid' | 'overdue';

/**
 * Derive the status of a financial record based on payment and due date.
 * 
 * Rules (with overdue precedence):
 * 1. If paid ≥ amount → 'paid' (even when past due)
 * 2. Else if past due and not fully paid → 'overdue' (takes precedence)
 * 3. Else if 0 < paid < amount → 'partial'
 * 4. Else (paid == 0, not past due) → 'pending'
 * 
 * @param amountPaidPence - Total recorded payment in pence
 * @param amountPence - Record amount in pence
 * @param dueDateIso - Due date as ISO string (e.g., "2024-12-31")
 * @param now - Current date for comparison
 * @returns The derived status
 */
export function deriveStatus(
  amountPaidPence: number,
  amountPence: number,
  dueDateIso: string,
  now: Date,
): FinancialRecordStatusType {
  // Rule 1: If paid >= amount, it's paid (even if past due)
  if (amountPaidPence >= amountPence) {
    return 'paid';
  }

  // Parse due date
  const dueDate = new Date(dueDateIso);
  const isPastDue = dueDate < now;

  // Rule 2: If past due and not fully paid, it's overdue (precedence over pending/partial)
  if (isPastDue && amountPaidPence < amountPence) {
    return 'overdue';
  }

  // Rule 3: If 0 < paid < amount, it's partial
  if (amountPaidPence > 0 && amountPaidPence < amountPence) {
    return 'partial';
  }

  // Rule 4: Otherwise (paid == 0, not past due), it's pending
  return 'pending';
}
